import { useMemo } from "react";

import { GridRowsProp } from "@mui/x-data-grid";

import { eventType } from "../../../../../API";
import { nameParser } from "../../../../../common/utils/nameParser";
import {
  ISystemPerformanceTableRow,
  SystemPerformanceResponseModel,
} from "../../system-performance/models/chart";
import { extractNodeName } from "../common/helpers";

export const useSystemHealthTableRows = (
  data: SystemPerformanceResponseModel[]
) => {
  const rows = useMemo((): GridRowsProp<ISystemPerformanceTableRow> => {
    const grouped: Record<
      string,
      Partial<ISystemPerformanceTableRow> & {
        entries: SystemPerformanceResponseModel[];
      }
    > = {};

    data.forEach(item => {
      const { id } = item;

      if (!id) {
        return;
      }

      if (!grouped[id]) {
        let itemName = "";

        if (
          item.event_type === eventType.nodeUptime ||
          item.event_type === eventType.bandwidthFailures
        ) {
          itemName = nameParser.node(id);
        }

        if (item.event_type === eventType.deviceUptime) {
          itemName = nameParser.device(id);
        }

        if (item.event_type === eventType.serviceUptime) {
          itemName = nameParser.serviceWithAbbreviation(id);
        }

        grouped[id] = {
          rowId: id,
          itemName,
          entries: [],
        };
      }

      grouped[id].entries.push(item);
    });

    const result = Object.values(grouped)
      .map(grp => {
        const base: { [key: string]: string } = {
          rowId: grp.rowId!,
          itemName: grp.itemName!,
        };

        grp.entries.forEach(item => {
          let dateStr: string;

          const dateField =
            item.event_type === eventType.bandwidthFailures
              ? (item as any).day_start
              : (item as any).week_start;

          if (typeof dateField === "string") {
            dateStr = dateField.slice(0, 10);
          } else if (dateField instanceof Date) {
            dateStr = dateField.toISOString().slice(0, 10);
          } else {
            return;
          }

          const fieldKey = `uptimePct-${dateStr}`;
          let raw: string | null = null;
          const sign = "%";

          if ("uptime_pct" in item) {
            // Check if uptime_pct exists and has a meaningful value
            if (
              item.uptime_pct !== undefined &&
              item.uptime_pct !== null &&
              item.uptime_pct !== ""
            ) {
              raw = item.uptime_pct;
            } else {
              raw = null; // This will result in "-"
            }
          } else if ("intervals_with_failures_pct" in item) {
            // Check if intervals_with_failures_pct exists and has a meaningful value
            if (
              item.intervals_with_failures_pct !== undefined &&
              item.intervals_with_failures_pct !== null &&
              item.intervals_with_failures_pct !== ""
            ) {
              raw = item.intervals_with_failures_pct;
            } else {
              raw = null; // This will result in "-"
            }
          } else {
            raw = null; // This will result in "-"
          }

          let formatted: string;

          if (raw !== null) {
            const num = parseFloat(raw);

            if (!isNaN(num)) {
              formatted = `${Math.round(num)}${sign}`;
            } else {
              formatted = "-"; // Invalid number
            }
          } else {
            formatted = "-"; // No data
          }

          base[fieldKey] = formatted;
        });

        return base as unknown as ISystemPerformanceTableRow;
      })
      .sort((a, b) => {
        const hasData = (row: ISystemPerformanceTableRow) =>
          Object.entries(row).some(
            ([key, value]) =>
              key.startsWith("uptimePct-") &&
              value !== undefined &&
              value !== null &&
              value !== "-" &&
              value !== ""
          );

        const aHasData = hasData(a);
        const bHasData = hasData(b);

        if (aHasData !== bHasData) return aHasData ? -1 : 1;

        return extractNodeName(a.itemName).localeCompare(
          extractNodeName(b.itemName),
          undefined,
          { sensitivity: "base" }
        );
      });

    return result;
  }, [data]);

  return {
    rows,
  };
};
